import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const COBITReport = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const reportRef = useRef(null);

  // Success toast message
  useEffect(() => {
    if (location.state?.showSuccess) {
      toast.success(
        "🎉 Well done! Your COBIT 2019 report is available in the Reports section.",
        { position: "top-right", autoClose: 5000 }
      );
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Fetch report
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setError("Not authenticated");
      setLoading(false);
      return;
    }

    axios
      .get(`${process.env.REACT_APP_API_URL}maturity/report/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setReportData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(
          err.response?.data?.detail || "Failed to load assessment report."
        );
        setLoading(false);
      });
  }, []);

  // Export PDF
  const handleExportPDF = () => {
    setExporting(true);
    const input = reportRef.current;
    const now = new Date();

    html2canvas(input, { scale: 2 })
      .then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 10;
        const startY = 15;
        const usablePageHeight = pageHeight - startY - margin;

        pdf.setFontSize(18);
        pdf.setTextColor(255, 77, 0);
        pdf.text("Assessment Report", pageWidth / 2, startY - 5, {
          align: "center",
        });

        pdf.setFontSize(10);
        pdf.setTextColor(100);
        pdf.text(`Generated on: ${now.toLocaleString()}`, margin, startY + 5);

        const imgWidth = pageWidth - 2 * margin;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (imgHeight <= usablePageHeight) {
          pdf.addImage(imgData, "PNG", margin, startY + 10, imgWidth, imgHeight);
        } else {
          let yOffset = 0;
          const totalHeight = canvas.height;
          const pageHeightPx = (usablePageHeight * canvas.width) / imgWidth;

          while (yOffset < totalHeight) {
            const sliceCanvas = document.createElement("canvas");
            sliceCanvas.width = canvas.width;
            sliceCanvas.height = Math.min(
              canvas.height - yOffset,
              pageHeightPx
            );
            const ctx = sliceCanvas.getContext("2d");
            ctx.drawImage(
              canvas,
              0,
              yOffset,
              canvas.width,
              sliceCanvas.height,
              0,
              0,
              sliceCanvas.width,
              sliceCanvas.height
            );

            const pageImage = sliceCanvas.toDataURL("image/png");
            const displayHeight =
              (sliceCanvas.height * imgWidth) / canvas.width;

            if (yOffset > 0) pdf.addPage();

            pdf.addImage(
              pageImage,
              "PNG",
              margin,
              startY + 10,
              imgWidth,
              displayHeight
            );

            yOffset += sliceCanvas.height;
          }
        }

        pdf.save(`assessment-report-${now.toISOString().split("T")[0]}.pdf`);
        setExporting(false);
      })
      .catch((err) => {
        console.error("PDF export error:", err);
        setExporting(false);
      });
  };

  // Parse the numeric level
  const getLevelLabel = (level) => {
    const num =
      typeof level === "string"
        ? parseInt(level.split(" - ")[0], 10)
        : level;

    switch (num) {
      case 0:
        return "0 - Incomplete / Not Performed";
      case 1:
        return "1 - Initial";
      case 2:
        return "2 - Managed";
      case 3:
        return "3 - Defined";
      default:
        return "Unknown";
    }
  };

  if (loading) return <p>Loading report...</p>;
  if (error) return <p className="text-red-600">{error}</p>;

  const completedDomains = reportData.domains || [];

  const chartData = completedDomains.map((domain) => ({
    domain: `${domain.domain} – ${domain.domain_title}`,
    compliance: Number(domain.compliance_percent) || 0,
  }));


  return (
    <div className="max-w-6xl mx-auto p-6">
      <div ref={reportRef}>
        <h1 className="text-3xl font-bold mb-6 text-[#FF4D00]">
          Assessment Report
        </h1>

        {/* --- Domain Summary Table --- */}
        <div className="mb-8 p-4 bg-gray-100 rounded shadow">
          <h2 className="text-xl font-semibold mb-4">Domain Summary</h2>
          <table className="w-full border-collapse border border-gray-300 mb-4">
            <thead>
              <tr>
                <th className="border px-2 py-1 bg-gray-200">Domain</th>
                <th className="border px-2 py-1 bg-gray-200">Title</th>
                <th className="border px-2 py-1 bg-gray-200">Total Controls</th>
                <th className="border px-2 py-1 bg-gray-200">Non-compliance</th>
                <th className="border px-2 py-1 bg-gray-200">Compliance %</th>
                <th className="border px-2 py-1 bg-gray-200">Current Level</th>
                <th className="border px-2 py-1 bg-gray-200">Target Level</th>
              </tr>
            </thead>
            <tbody>
              {completedDomains.map((domain) => (
                <tr key={domain.domain}>
                  <td className="border px-2 py-1">{domain.domain}</td>
                  <td className="border px-2 py-1">{domain.domain_title}</td>
                  <td className="border px-2 py-1">{domain.total_controls}</td>
                  <td className="border px-2 py-1">
                    {domain.total_controls - domain.compliant_controls}
                  </td>
                  <td className="border px-2 py-1">
                    {domain.compliance_percent.toFixed(2)}%
                  </td>
                  <td className="border px-2 py-1">
                    {getLevelLabel(domain.current_level)}
                  </td>
                  <td className="border px-2 py-1">
                    {getLevelLabel(domain.target_level)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* --- Details for each domain --- */}
        {completedDomains.map((domain) => (
          <div key={domain.domain} className="mb-8 border-b pb-4">
            <h2 className="text-xl font-semibold mb-2">
              {domain.domain} - {domain.domain_title}
            </h2>

            {domain.practices?.map((practice) => (
              <div
                key={practice.practice}
                className="mb-6 pl-4 border-l-4 border-orange-400"
              >
                <h3 className="text-lg font-semibold mb-2">
                  {practice.practice} - {practice.practice_title}
                </h3>

                {practice.questions?.map((q) => (
                  <div
                    key={q.id}
                    className="mt-4 bg-orange-50 p-4 rounded border border-orange-200"
                  >
                    <p className="font-semibold mb-2">Question: {q.request}</p>

                    {q.gaps?.map((rec, idx) => (
                      <div key={idx} className="mb-2">
                        <p>
                          <strong>Gap:</strong> {rec.gap}
                        </p>
                        <p>
                          <strong>Recommendation:</strong>{" "}
                          {rec.recommendation}
                        </p>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}

        {/* --- Chart --- */}
        {completedDomains.length > 0 && (
          <>
            <h2 className="text-2xl font-bold mt-10 mb-4 text-[#FF4D00]">
              Maturity Score Visualization
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-4 shadow rounded">
                <h3 className="text-lg font-semibold mb-2">
                  Compliance Percentage by Domain
                </h3>

                <div className="overflow-x-auto">
                  {/* Force wider chart so labels can breathe */}
                  <div style={{ minWidth: `${chartData.length * 110}px` }}>
                    <ResponsiveContainer width="100%" height={320}>
                      <BarChart
                        data={chartData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 90 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />

                        <XAxis
                          dataKey="domain"
                          angle={-45}
                          textAnchor="end"
                          interval={0}
                          height={90}       // more space for labels
                          style={{ fontSize: 10 }}
                        />

                        <YAxis domain={[0, 100]} />
                        <Tooltip />

                        <Bar dataKey="compliance" fill="#FF4D00" barSize={28}>
                          <LabelList
                            dataKey="compliance"
                            position="insideTop"
                            formatter={(v) => `${v}%`}
                            style={{ fill: "#fff", fontSize: 16, fontWeight: "bold" }}
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4 mt-8">
        <button
          onClick={() => navigate("/cobit/dashboard")}
          className="bg-[#FF4D00] text-white px-4 py-2 rounded hover:bg-orange-700"
        >
          View Dashboard
        </button>

        <button
          onClick={handleExportPDF}
          disabled={exporting}
          className={`bg-[#FF4D00] text-white px-4 py-2 rounded ${
            exporting ? "opacity-50 cursor-not-allowed" : "hover:bg-orange-700"
          }`}
        >
          {exporting ? "Generating..." : "Export PDF"}
        </button>
      </div>
    </div>
  );
};

export default COBITReport;
